import React, { useState, useMemo } from 'react';
import { ChevronLeft, Copy, Volume2, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Summary, DetailedSummaryContent, DetailedBulletPoint } from '@/features/summaries/types';

interface DetailedSummaryViewerProps {
  summary: Summary;
  onBack: () => void;
}

export const DetailedSummaryViewer: React.FC<DetailedSummaryViewerProps> = ({ summary, onBack }) => {
  const [copiedText, setCopiedText] = useState('');
  const [expandedCitations, setExpandedCitations] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const toggleCitation = (id: string) => {
    setExpandedCitations(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const parsedContent = useMemo((): DetailedSummaryContent => {
    if (!summary.content) {
      return { sections: [] };
    }
    try {
      return JSON.parse(summary.content) as DetailedSummaryContent;
    } catch {
      // Fallback for plain text
      return {
        sections: [{
          title: 'Summary',
          bulletPoints: summary.content.split('\n').filter(Boolean).map(text => ({ text }))
        }]
      };
    }
  }, [summary.content]);

  const getFormattedText = (): string => {
    return parsedContent.sections.map(section => {
      const bullets = section.bulletPoints.map(bp => `• ${bp.text}`).join('\n');
      return `${section.title}\n${bullets}`;
    }).join('\n\n');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getFormattedText());
    setCopiedText('Copied!');
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handleAudio = () => {
    toast({
      title: "Coming soon",
      description: "Text-to-speech will be available soon.",
    });
  };

  const handleSaveNote = () => {
    toast({
      title: "Coming soon",
      description: "Save as note will be available soon.",
    });
  };

  const handleDownload = () => {
    const blob = new Blob([getFormattedText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${summary.title || 'detailed-summary'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const CitationBadge: React.FC<{ count?: number; expandable?: boolean; citationId?: string }> = ({ 
    count = 1, 
    expandable = false, 
    citationId 
  }) => {
    if (expandable && citationId) {
      return (
        <sup 
          onClick={() => toggleCitation(citationId)}
          className="mr-1 cursor-pointer rounded-full bg-primary/10 px-[5px] py-[2px] text-[10px] text-primary transition-colors hover:bg-primary/20 ml-0.5"
        >
          ...
        </sup>
      );
    }

    return (
      <>
        {[...Array(count)].map((_, i) => (
          <span key={i} className="inline">
            <sup className="cursor-pointer rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary transition-colors hover:bg-primary/20 ml-0.5">
              s
            </sup>
          </span>
        ))}
      </>
    );
  };

  const renderTextWithFormatting = (text: string): React.ReactNode[] => {
    // Handle <strong>, <sub> tags
    const parts = text.split(/(<strong>.*?<\/strong>|<sub>.*?<\/sub>)/g);
    return parts.map((part, index) => {
      if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
        const content = part.slice(8, -9);
        return <strong key={index} className="font-semibold">{content}</strong>;
      }
      if (part.startsWith('<sub>') && part.endsWith('</sub>')) {
        const content = part.slice(5, -6);
        return <sub key={index}>{content}</sub>;
      }
      return part;
    });
  };

  const renderBulletPoint = (
    bullet: DetailedBulletPoint, 
    index: number, 
    depth: number = 0
  ): React.ReactNode => {
    const textColorClass = depth === 0 
      ? 'text-foreground' 
      : 'text-muted-foreground';

    return (
      <li key={index} className={`leading-7 ${textColorClass}`}>
        {renderTextWithFormatting(bullet.text)}
        {bullet.citations && <CitationBadge count={bullet.citations} />}
        {bullet.expandableCitation && bullet.citationId && (
          <CitationBadge expandable citationId={bullet.citationId} />
        )}
        
        {bullet.children && bullet.children.length > 0 && (
          bullet.childListType === 'decimal' ? (
            <ol className="list-outside list-decimal pl-7 mt-2 space-y-2">
              {bullet.children.map((child, childIndex) => 
                renderBulletPoint(child, childIndex, depth + 1)
              )}
            </ol>
          ) : (
            <ul className="list-outside list-disc pl-5 mt-2 space-y-2">
              {bullet.children.map((child, childIndex) => 
                renderBulletPoint(child, childIndex, depth + 1)
              )}
            </ul>
          )
        )}
      </li>
    );
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Back Button - Sticky */}
      <div className="sticky top-0 z-10 shrink-0 bg-background px-4 pb-2 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex items-center gap-2 p-0 h-fit text-muted-foreground hover:text-foreground hover:bg-transparent"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </Button>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 overflow-y-auto pb-20">
        <div className="px-4">
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <div className="space-y-4">
              {parsedContent.sections.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No content available</p>
              ) : (
                parsedContent.sections.map((section, sectionIndex) => (
                  <React.Fragment key={sectionIndex}>
                    <h2 className={`mb-3 text-xl font-semibold text-foreground ${sectionIndex === 0 ? 'mt-0' : 'mt-6'}`}>
                      {section.title}
                    </h2>
                    <ul className="list-outside list-disc pl-5 space-y-3">
                      {section.bulletPoints.map((bullet, bulletIndex) => 
                        renderBulletPoint(bullet, bulletIndex, 0)
                      )}
                    </ul>
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar - Bottom */}
      <div className="py-3 px-4 bg-background">
        <div className="flex flex-row items-center justify-between space-x-1">
          <div className="flex flex-row items-center space-x-1">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Copy to clipboard"
            >
              <Copy className="h-4 w-4" />
            </button>

            {/* Audio Button */}
            <button
              onClick={handleAudio}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Read aloud"
            >
              <Volume2 className="h-4 w-4" />
            </button>

            {/* Save as Note Button */}
            <button
              onClick={handleSaveNote}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Save as note"
            >
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 256 256" className="h-4 w-4" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                <path d="M84,108A12,12,0,0,1,96,96h64a12,12,0,0,1,0,24H96A12,12,0,0,1,84,108Zm32,28H96a12,12,0,0,0,0,24h20a12,12,0,0,0,0-24ZM228,48V156.69a19.86,19.86,0,0,1-5.86,14.14l-51.31,51.31A19.86,19.86,0,0,1,156.69,228H48a20,20,0,0,1-20-20V48A20,20,0,0,1,48,28H208A20,20,0,0,1,228,48ZM52,204h92V156a12,12,0,0,1,12-12h48V52H52Zm139-36H168v23Z"></path>
              </svg>
            </button>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Download"
            >
              <FileDown className="h-4 w-4" />
            </button>
          </div>

          {/* Copy confirmation text */}
          {copiedText && (
            <span className="text-xs text-correct animate-in fade-in-0">
              {copiedText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
